import React, { useContext } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  Typography,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
  Divider,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { LoginCardModalContext } from "../../App";

// ✅ Validation schema
const validationSchema = Yup.object({
  fullName: Yup.string().min(2).required("Full name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]+$/, "Only digits are allowed")
    .min(7, "Must be at least 10 digits")
    .required("Phone number is required"),
  // estimatedAmount: Yup.string().required("Estimated amount is required"),
  experience: Yup.string().required("Experience is required"),
});

const SignUpCard = () => {
  const { closeLoginCardModal } = useContext(LoginCardModalContext);
  
  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      phone: "",
      // estimatedAmount: "",
      experience: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Split full name into first and last name
        const nameParts = values.fullName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Prepare form data for Zoho CRM
        const formData = new URLSearchParams();
        formData.append('xnQsjsdp', '313fcab0c2b9b7a894a9c3340d72a371ff35bc036b16b871e3fe596346c48513');
        formData.append('xmIwtLD', 'b95ac14fec724dfc440a5d840a11cf83b52b075307eb50f2cda63d3392b84456e655387cfbde1575d157376d99d96fdd');
        formData.append('actionType', 'TGVhZHM=');
        formData.append('returnURL', 'https://portal.bbcorp.trade/auth/jwt/sign-up/');
        formData.append('First Name', firstName);
        formData.append('Last Name', lastName);
        formData.append('Email', values.email);
        formData.append('Phone', values.phone);
        formData.append('LEADCF51', '5');
        formData.append('Lead Source', 'Web Research');
        formData.append('Lead Status', 'New Lead');
        
        // Submit to Zoho CRM
        const response = await fetch('https://crm.zoho.com/crm/WebToLeadForm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData
        });
        
        if (response.ok) {
          // Open portal in new window
          const win = window.open("https://portal.bbcorp.trade/auth/jwt/sign-up", "_blank");
          resetForm();
          // Close the modal after successful submission
          closeLoginCardModal();
        } else {
          console.error('Failed to submit to Zoho CRM');
        }
      } catch (error) {
        console.error("Network error:", error);
      }
    },
  });

  return (
    <Box
      sx={{
        background: 'transparent',
        borderRadius: 4,
        p: { xs: 2, md: 4 },
        mt:2,
        mb:2,
        boxShadow: 'none',
        maxWidth: 480,
        mx: 'auto',
      }}
    >
      {/* Accent bar */}
      <Box sx={{ width: 40, height: 4, bgcolor: '#19ABE2', borderRadius: 2, mx: 'auto', mb: 2 }} />
      <Typography
        variant="h5"
        fontWeight={700}
        color="#2494C9"
        textAlign="center"
        mb={0.5}
        letterSpacing={1}
        sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
      >
        Create Your Account
      </Typography>
      <Typography
        variant="subtitle1"
        color="#6B7280"
        textAlign="center"
        mb={3}
        fontWeight={400}
        sx={{ fontSize: { xs: '1rem', md: '1.15rem' } }}
      >
        Sign up to get started with BBCorp
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <form onSubmit={formik.handleSubmit}>
        {['fullName', 'email'].map((field) => (
          <Box key={field} mb={2.5}>
            <Typography
              variant="body2"
              mb={0.5}
              color="#667085"
              fontWeight={600}
            >
              {field === 'fullName'
                ? 'Full Name'
                : field === 'email'
                ? 'Email'
                : 'Phone Number'}
            </Typography>
            <TextField
              fullWidth
              size="medium"
              name={field}
              type={field === 'email' ? 'email' : 'text'}
              placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
              value={formik.values[field]}
              onChange={formik.handleChange}
              error={formik.touched[field] && Boolean(formik.errors[field])}
              helperText={formik.touched[field] && formik.errors[field]}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 2.5,
                  background: '#F7F9FB',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 1.5,
                  boxShadow: '0 1px 4px 0 rgba(36, 148, 201, 0.06)',
                  border: '1px solid #E0E7EF',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 2px 8px 0 rgba(36, 148, 201, 0.10)',
                  },
                },
                '& input': {
                  fontSize: '14px',
                  fontWeight: 400,
                },
                '& input::placeholder': {
                  color: '#B0B8C1',
                  opacity: 1,
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        ))}
        <Box mb={2.5}>
          <Typography
            variant="body2"
            mb={0.5}
            color="#667085"
            fontWeight={600}
          >
            Phone Number
          </Typography>
          <PhoneInput
            country={'us'}
            value={formik.values.phone}
            onChange={value => formik.setFieldValue('phone', value)}
            inputProps={{
              name: 'phone',
              required: true,
              autoFocus: false,
            }}
            inputStyle={{
              width: '100%',
              borderRadius: 8,
              background: '#F7F9FB',
              fontSize: 16,
              border: '1px solid #E0E7EF',
              paddingLeft: 48,
              height: 44,
              color: '#222',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
            buttonStyle={{
              borderRadius: 8,
              border: '1px solid #E0E7EF',
              background: '#F7F9FB',
              marginRight: 8,
              height: 44,
              width: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
            containerStyle={{ width: '100%' }}
            dropdownStyle={{
              borderRadius: 12,
              boxShadow: '0 8px 32px 0 rgba(36, 148, 201, 0.18)',
              fontSize: 15,
              fontFamily: 'inherit',
              background: '#fff',
              color: '#222',
              zIndex: 9999,
              marginTop: 4,
            }}
            specialLabel={''}
            enableSearch
            disableSearchIcon={false}
            countryCodeEditable={true}
            masks={{us: '(...) ...-....'}}
          />
          {formik.touched.phone && formik.errors.phone && (
            <FormHelperText error>{formik.errors.phone}</FormHelperText>
          )}
        </Box>
        <Box mb={3}>
          <Typography
            variant="body2"
            mb={0.5}
            color="#667085"
            fontWeight={600}
          >
            Years of Experience
          </Typography>
          <FormControl
            fullWidth
            size="medium"
            error={formik.touched.experience && Boolean(formik.errors.experience)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                background: '#F7F9FB',
                boxShadow: '0 1px 4px 0 rgba(36, 148, 201, 0.06)',
                border: '1px solid #E0E7EF',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: '0 2px 8px 0 rgba(36, 148, 201, 0.10)',
                },
              },
              '& .MuiSelect-select': {
                fontSize: '14px',
                fontWeight: 400,
              },
            }}
          >
            <Select
              name="experience"
              value={formik.values.experience}
              onChange={formik.handleChange}
              displayEmpty
              sx={{
                fontSize: '14px',
              }}
            >
              <MenuItem disabled value="">
                <span style={{ color: '#B0B8C1', fontSize: '14px' }}>Select years</span>
              </MenuItem>
              <MenuItem value="0-1" sx={{ fontSize: '14px' }}>0–1 years</MenuItem>
              <MenuItem value="1-3" sx={{ fontSize: '14px' }}>1–3 years</MenuItem>
              <MenuItem value="3-5" sx={{ fontSize: '14px' }}>3–5 years</MenuItem>
              <MenuItem value=">5" sx={{ fontSize: '14px' }}>&gt; 5 years</MenuItem>
            </Select>
            <FormHelperText>
              {formik.touched.experience && formik.errors.experience}
            </FormHelperText>
          </FormControl>
        </Box>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!(formik.isValid && formik.dirty)}
          sx={{
            mt: 2,
            py: 1.7,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '15px',
            background: 'linear-gradient(90deg, #2494C9 0%, #19ABE2 100%)',
            boxShadow: '0 2px 12px 0 rgba(36, 148, 201, 0.13)',
            textTransform: 'none',
            letterSpacing: 0.5,
            transition: 'background 0.3s, box-shadow 0.3s',
            '&:hover': {
              background: 'linear-gradient(90deg, #19ABE2 0%, #2494C9 100%)',
              boxShadow: '0 4px 16px 0 rgba(36, 148, 201, 0.18)',
            },
            opacity: !(formik.isValid && formik.dirty) ? 0.6 : 1,
            pointerEvents: !(formik.isValid && formik.dirty) ? 'none' : 'auto',
          }}
        >
          Sign Up
        </Button>
      </form>
    </Box>
  );
};

export default SignUpCard;